import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeIcon, MapPinIcon, BuildingOfficeIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const ContactUsPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const offices = [
    {
      icon: BuildingOfficeIcon,
      title: "UK Office",
      address: [
        "Bureau, 90 Fetter Lane",
        "London, United Kingdom",
        "EC4A 1EN"
      ],
      phone: null,
      email: "info@blockfirstcapital.com"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-white pt-20 lg:pt-24 pb-16 lg:pb-24 overflow-hidden">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 font-heading"
            >
              Get in <span className="text-amber-600">Touch</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl lg:text-2xl text-gray-600 leading-relaxed"
            >
              Have questions about our investment services? We're here to help you achieve your financial goals.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16 lg:mb-20"
          >
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Our Offices
            </h2>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Connect with our teams around the world for personalized investment solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {offices.map((office, index) => {
              const IconComponent = office.icon;
              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94] 
                  }}
                  viewport={{ once: true }}
                  className="group bg-gray-50 rounded-3xl p-8 lg:p-10 hover:bg-white hover:shadow-xl transition-all duration-500 border border-gray-100"
                >
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center group-hover:bg-amber-600 transition-colors duration-500 flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors duration-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 font-heading">
                        {office.title}
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-start space-x-4">
                          <MapPinIcon className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                          <div className="text-gray-600 leading-relaxed">
                            {office.address.map((line, idx) => (
                              <div key={idx} className="text-lg">{line}</div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Phone removed by request */}
                        
                        {office.email && (
                          <div className="flex items-center space-x-4">
                            <EnvelopeIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <a 
                              href={`mailto:${office.email}`}
                              className="text-lg text-gray-600 hover:text-amber-600 transition-colors"
                            >
                              {office.email}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Column - Info */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 font-heading leading-tight">
                  Let's Start a Conversation
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Ready to take control of your financial future? Our expert team is standing by to provide personalized investment guidance tailored to your unique goals.
                </p>
              </div>

              <div className="space-y-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 border border-gray-100"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading">Quick Response</h3>
                  <p className="text-gray-600">We typically respond to all inquiries within 24 hours during business days.</p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 border border-gray-100"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading">Expert Guidance</h3>
                  <p className="text-gray-600">Connect directly with our experienced investment professionals and financial advisors.</p>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-amber-600 text-white rounded-2xl p-6"
                >
                  <h3 className="text-xl font-bold mb-3 font-heading">Important Notice</h3>
                  <p className="text-amber-100 text-sm leading-relaxed">
                    Blockfirst Capital is committed to providing transparent and compliant investment services. The information contained on this site is not to be construed as advice unless specifically referred to as Advice.
                  </p>
                </motion.div>
              </div>
            </motion.div>
            
            {/* Right Column - Form */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-xl p-8 lg:p-10 border border-gray-100"
            >
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8 font-heading">Send us a Message</h3>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-3">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      className="w-full px-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200 text-lg"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-3">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      className="w-full px-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200 text-lg"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-3">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    className="w-full px-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200 text-lg"
                    placeholder="How can we help you?"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-3">Message</label>
                  <textarea
                    id="message"
                    rows="6"
                    className="w-full px-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200 text-lg resize-none"
                    placeholder="Tell us about your investment goals and how we can assist you..."
                  ></textarea>
                </div>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 px-6 bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg rounded-2xl transition-colors inline-flex items-center justify-center gap-3"
                >
                  Send Message
                  <ArrowRightIcon className="w-5 h-5" />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUsPage;
